import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DialogEyebrow } from '@/components/ui/dialog-eyebrow';
import { AlertCircle, Lightbulb, Crown, Loader2, MessageCircle, Receipt, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';

interface SupportPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userEmail?: string;
  /** Premium/Pro/Enterprise — unlocks the priority-support topic. */
  isPremium?: boolean;
  /** Optional auto-captured screenshot (data URL) to attach to a problem/priority report. */
  screenshot?: string;
  /** 'support' (in-app, uses userEmail) | 'contact' (marketing, logged-out: collects email/name, sales-aware topics). */
  variant?: 'support' | 'contact';
  /** Subject the team sees for an in-app problem report, in place of "Problem Report". */
  reportName?: string;
  /** Text the sender may attach (on by default), such as the assistant's last answer. */
  attachment?: { label: string; text: string };
}

type Topic = 'problem' | 'feature' | 'priority' | 'general' | 'pricing' | 'enterprise';

/** Email subject/body label per contact topic (report-issue `name`). */
const CONTACT_LABELS: Record<string, string> = {
  general: 'General',
  pricing: 'Pricing',
  enterprise: 'Enterprise/Sales',
  problem: 'Problem',
};

/**
 * Unified "send us a ticket" panel — replaces the three separate
 * ReportProblem / RequestFeature / PrioritySupport dialogs with one editorial
 * panel. The topic selector routes to the same backends those dialogs used, so
 * delivery (report-issue email + submit-feedback DB/digest) is unchanged:
 *   - problem  → report-issue   (name "Problem Report")
 *   - feature  → submit-feedback (persists to feature_requests, then emails)
 *   - priority → report-issue   (name "[PRIORITY] Support Request", premium)
 */
export const SupportPanel = ({ open, onOpenChange, userEmail, isPremium, screenshot, variant = 'support', reportName, attachment }: SupportPanelProps) => {
  const { t } = useTranslation(['dashboard', 'common', 'translation']);
  const isContact = variant === 'contact';
  const [topic, setTopic] = useState<Topic>(isContact ? 'general' : 'problem');
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [attachShot, setAttachShot] = useState(true);
  const [attachText, setAttachText] = useState(true);
  // An attached conversation is a report on its own; a note is optional then.
  const attached = attachment && attachText ? attachment.text : '';
  const body = [message.trim(), attached].filter(Boolean).join('\n\n---\n');
  // Contact mode (logged-out marketing) collects the visitor's own details.
  const [contactEmail, setContactEmail] = useState(userEmail ?? '');
  const [contactName, setContactName] = useState('');

  type TopicDef = { key: Topic; label: string; desc: string; Icon: typeof AlertCircle; iconClass: string };
  const supportTopics: TopicDef[] = [
    { key: 'problem', label: t('dashboard:report_problem_title', { defaultValue: 'Report a problem' }), desc: t('dashboard:support_type_problem_desc', { defaultValue: "Something's broken or not working right." }), Icon: AlertCircle, iconClass: 'text-[var(--color-brand-blue-text)]' },
    { key: 'feature', label: t('dashboard:request_feature_title', { defaultValue: 'Request a feature' }), desc: t('dashboard:support_type_feature_desc', { defaultValue: 'Suggest an improvement or something new.' }), Icon: Lightbulb, iconClass: 'text-[var(--color-brand-blue-text)]' },
    ...(isPremium ? [{ key: 'priority' as Topic, label: t('priority_support', { defaultValue: 'Priority support' }), desc: t('dashboard:support_type_priority_desc', { defaultValue: 'Fast-tracked straight to our team.' }), Icon: Crown, iconClass: 'text-amber-500' }] : []),
  ];
  const contactTopics: TopicDef[] = [
    { key: 'general', label: t('contact_topic_general', { defaultValue: 'General enquiry' }), desc: t('contact_topic_general_desc', { defaultValue: 'Questions about the product or anything else.' }), Icon: MessageCircle, iconClass: 'text-[var(--color-brand-blue-text)]' },
    { key: 'pricing', label: t('contact_topic_pricing', { defaultValue: 'Pricing & plans' }), desc: t('contact_topic_pricing_desc', { defaultValue: 'Plans, billing, discounts.' }), Icon: Receipt, iconClass: 'text-[var(--color-brand-blue-text)]' },
    { key: 'enterprise', label: t('contact_topic_enterprise', { defaultValue: 'Enterprise / sales' }), desc: t('contact_topic_enterprise_desc', { defaultValue: 'Teams, white-label, custom domain, demo.' }), Icon: Building2, iconClass: 'text-amber-500' },
    { key: 'problem', label: t('dashboard:report_problem_title', { defaultValue: 'Report a problem' }), desc: t('dashboard:support_type_problem_desc', { defaultValue: "Something's broken or not working right." }), Icon: AlertCircle, iconClass: 'text-[var(--color-brand-blue-text)]' },
  ];
  const topics = isContact ? contactTopics : supportTopics;

  const placeholder = isContact
    ? t('contact_placeholder', { defaultValue: 'Tell us what you need — your project, team size, timeline…' })
    : topic === 'feature'
      ? t('dashboard:request_feature_placeholder', { defaultValue: 'Describe the feature you would like…' })
      : t('dashboard:report_problem_placeholder', { defaultValue: 'Describe what happened…' });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const email = (isContact ? contactEmail : userEmail)?.trim();
    if (!email || !body) {
      toast.error(t('dashboard:fill_all_fields'));
      return;
    }
    if (isContact && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      toast.error(t('contact_invalid_email', { defaultValue: 'Please enter a valid email address.' }));
      return;
    }

    setIsLoading(true);
    try {
      let error;
      if (!isContact && topic === 'feature') {
        // submit-feedback persists to feature_requests FIRST, then best-effort
        // emails — so input survives a failed send (daily digest backstops it).
        ({ error } = await supabase.functions.invoke('submit-feedback', {
          body: { message: body, email, source: 'dashboard_dialog', ...(screenshot && attachShot ? { screenshot } : {}) },
        }));
      } else {
        // Everything else (in-app problem/priority AND all contact topics) goes
        // to report-issue, which emails the team and accepts a screenshot.
        const subject = isContact
          ? `Contact (${CONTACT_LABELS[topic] ?? topic})${contactName.trim() ? ' — ' + contactName.trim() : ''}`
          : (topic === 'priority' ? '[PRIORITY] Support Request' : reportName ?? 'Problem Report');
        ({ error } = await supabase.functions.invoke('report-issue', {
          body: {
            name: subject,
            email,
            message: body,
            ...(screenshot && attachShot ? { screenshot } : {}),
          },
        }));
      }

      if (error) {
        console.error('SupportPanel submit error:', error);
        toast.error(isContact
          ? t('contact_error', { defaultValue: 'Could not send — please email contact@cctvplanner.io.' })
          : t(topic === 'feature' ? 'dashboard:request_feature_error' : 'dashboard:report_problem_error'));
        return;
      }

      toast.success(isContact
        ? t('contact_sent', { defaultValue: "Thanks — we'll get back to you shortly." })
        : t(topic === 'feature' ? 'dashboard:request_feature_submitted' : 'dashboard:report_problem_submitted'));
      setMessage('');
      if (isContact) setContactName('');
      onOpenChange(false);
    } catch (err) {
      console.error('SupportPanel submit error:', err);
      toast.error(isContact
        ? t('contact_error', { defaultValue: 'Could not send — please email contact@cctvplanner.io.' })
        : t(topic === 'feature' ? 'dashboard:request_feature_error' : 'dashboard:report_problem_error'));
    } finally {
      setIsLoading(false);
    }
  };

  const sendLabel = isContact
    ? t('contact_send', { defaultValue: 'Send message' })
    : topic === 'feature'
      ? t('dashboard:submit_request', { defaultValue: 'Submit request' })
      : t('dashboard:send_report', { defaultValue: 'Send' });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="!rounded-none sm:max-w-lg bg-white dark:bg-graphite-900 border border-gray-200 dark:border-brand-border-soft p-0 shadow-none">
        <form onSubmit={handleSubmit} className="px-6 pt-6 pb-5">
          <DialogHeader className="mb-5">
            <DialogEyebrow>{isContact ? 'CONTACT' : 'SUPPORT'}</DialogEyebrow>
            <DialogTitle className="text-foreground dark:text-white text-base font-semibold tracking-tight">
              {isContact
                ? t('contact_panel_title', { defaultValue: 'Contact us' })
                : t('dashboard:support_panel_title', { defaultValue: 'How can we help?' })}
            </DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-neutral-400 text-sm leading-relaxed">
              {isContact
                ? t('contact_panel_subtitle', { defaultValue: "Tell us about your project — we'll get back to you." })
                : t('dashboard:support_panel_subtitle', { defaultValue: 'Pick a topic and send us a message — we read every one.' })}
            </DialogDescription>
          </DialogHeader>

          {/* Topic selector */}
          <div className="space-y-2 mb-5">
            {topics.map(({ key, label, desc, Icon, iconClass }) => {
              const active = topic === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTopic(key)}
                  aria-pressed={active}
                  className={`w-full flex items-start gap-3 px-3 py-2.5 text-left transition-colors border border-gray-200 dark:border-brand-border-soft ${
                    active
                      ? 'bg-[var(--color-brand-blue)]/[0.06] shadow-[inset_3px_0_0_0_var(--color-brand-blue)]'
                      : 'hover:bg-gray-50 dark:hover:bg-neutral-900'
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${iconClass}`} />
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm font-semibold text-foreground dark:text-white">{label}</span>
                    <span className="block text-xs text-gray-500 dark:text-neutral-400 leading-snug">{desc}</span>
                  </span>
                  <span
                    className={`mt-0.5 w-3.5 h-3.5 rounded-full border flex items-center justify-center shrink-0 transition-colors ${
                      active ? 'border-[var(--color-brand-blue)]' : 'border-gray-300 dark:border-neutral-600'
                    }`}
                  >
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-brand-blue)]" />}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Contact details — only in the logged-out marketing contact form. */}
          {isContact && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
              <input
                type="text"
                value={contactName}
                onChange={(e) => setContactName(e.target.value)}
                placeholder={t('contact_name_placeholder', { defaultValue: 'Your name (optional)' })}
                disabled={isLoading}
                className="px-3 py-2.5 border border-gray-200 dark:border-brand-border-strong bg-white dark:bg-graphite-800 text-foreground dark:text-white text-sm placeholder:text-gray-400 dark:placeholder:text-neutral-600 focus:border-[var(--color-brand-blue)] focus:outline-none"
              />
              <input
                type="email"
                required
                value={contactEmail}
                onChange={(e) => setContactEmail(e.target.value)}
                placeholder={t('contact_email_placeholder', { defaultValue: 'you@company.com' })}
                disabled={isLoading}
                className="px-3 py-2.5 border border-gray-200 dark:border-brand-border-strong bg-white dark:bg-graphite-800 text-foreground dark:text-white text-sm placeholder:text-gray-400 dark:placeholder:text-neutral-600 focus:border-[var(--color-brand-blue)] focus:outline-none"
              />
            </div>
          )}

          {/* Message */}
          <textarea
            placeholder={placeholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={isLoading}
            rows={4}
            autoFocus
            className="w-full px-3 py-2.5 border border-gray-200 dark:border-brand-border-strong bg-white dark:bg-graphite-800 text-foreground dark:text-white text-sm resize-none placeholder:text-gray-400 dark:placeholder:text-neutral-600 focus:border-[var(--color-brand-blue)] focus:outline-none"
          />

          {/* Auto-captured screenshot — attached to the email for every topic
              (report-issue for problem/priority, submit-feedback for features). */}
          {screenshot && (
            <label className="mt-3 flex items-center gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={attachShot}
                onChange={(e) => setAttachShot(e.target.checked)}
                className="accent-[var(--color-brand-blue)] w-3.5 h-3.5 shrink-0"
              />
              <img
                src={screenshot}
                alt=""
                className="w-16 h-10 object-cover border border-gray-200 dark:border-brand-border-soft shrink-0"
              />
              <span className="text-xs text-gray-600 dark:text-neutral-300">
                {t('support_attach_screenshot', { defaultValue: 'Attach screenshot of the current view' })}
              </span>
            </label>
          )}

          {attachment && (
            <label className="mt-3 flex items-start gap-3 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={attachText}
                onChange={(e) => setAttachText(e.target.checked)}
                className="accent-[var(--color-brand-blue)] w-3.5 h-3.5 mt-0.5 shrink-0"
              />
              <span className="min-w-0">
                <span className="block text-xs text-gray-600 dark:text-neutral-300">{attachment.label}</span>
                <span className="block text-[11px] text-gray-400 dark:text-neutral-500 line-clamp-2 break-words">{attachment.text}</span>
              </span>
            </label>
          )}

          {/* Sender + actions */}
          <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-[10px] font-mono uppercase tracking-[0.12em] text-gray-400 dark:text-neutral-500 truncate min-w-0" title={!isContact ? (userEmail || '') : ''}>
              {isContact
                ? t('contact_reply_note', { defaultValue: "We'll reply to the email above." })
                : `${t('dashboard:support_sent_as', { defaultValue: 'Sent as' })} ${userEmail || '—'}`}
            </span>
            <div className="flex items-center justify-end gap-2 shrink-0">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
                className="px-4 py-2.5 border border-gray-200 dark:border-brand-border-strong text-foreground dark:text-white hover:bg-gray-100 dark:hover:bg-neutral-900 text-xs font-mono uppercase tracking-wide transition-colors"
              >
                {t('dashboard:cancel_btn', { defaultValue: 'Cancel' })}
              </button>
              <button
                type="submit"
                disabled={isLoading || !body}
                className="inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-neutral-900 dark:bg-white hover:bg-neutral-800 dark:hover:bg-neutral-200 disabled:opacity-40 text-white dark:text-neutral-900 text-xs font-mono font-semibold uppercase tracking-wide transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    {t('dashboard:saving', { defaultValue: 'Sending…' })}
                  </>
                ) : (
                  <>
                    {topic === 'priority' && <Crown className="w-3.5 h-3.5" />}
                    {sendLabel} &rarr;
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
